// src/components/Citas/Citas.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Paper,
  Checkbox,
  FormControlLabel,
  Badge,
  Alert,
} from "@mui/material";
import { LocalizationProvider, DateCalendar, PickersDay, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

dayjs.extend(customParseFormat);

// -- Helpers --
const isoLocal = (d) => (d ? dayjs(d).format("YYYY-MM-DD[T]HH:mm:ss") : null);
const pick = (o, ...ks) => { for (const k of ks) if (o && o[k] != null) return o[k]; return null; };
// admite "YYYY-MM-DDTHH:mm:ss" y "YYYY-MM-DD HH:mm:ss"
const toDay = (val) => {
  if (!val) return null;
  const s = String(val);
  if (s.includes("T")) return dayjs(s);
  const p = dayjs(s, "YYYY-MM-DD HH:mm:ss", true);
  return p.isValid() ? p : dayjs(s);
};

const ESTADOS = ["DISPONIBLE", "PENDIENTE", "CONFIRMADA"];

export default function Citas() {
  const { usuario, token } = useAuth();

  const isAdmin = (usuario?.rol || "").toUpperCase() === "ADMIN";

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  // ---- Estado base ----
  const [todas, setTodas] = useState([]);            // todas las citas (GET /api/citas)
  const [loading, setLoading] = useState(true);

  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [monthCursor, setMonthCursor] = useState(null);

  const [mapCountByDay, setMapCountByDay] = useState({});
  const [huecosDia, setHuecosDia] = useState([]);
  const [slotSel, setSlotSel] = useState(null);

  // formulario
  const [conyuge, setConyuge] = useState(false);

  // ===== NUEVOS CAMPOS RENTA =====
  const [fechaCaducidadNif, setFechaCaducidadNif] = useState(null);
  const [referenciaBorrador, setReferenciaBorrador] = useState("");
  const [rentaAnterior, setRentaAnterior] = useState(false);

  // ===== DATOS CÓNYUGE =====
  const [nombreConyuge, setNombreConyuge] = useState("");
  const [apellidosConyuge, setApellidosConyuge] = useState("");
  const [nifConyuge, setNifConyuge] = useState("");
  const [fechaCaducidadNifConyuge, setFechaCaducidadNifConyuge] = useState(null);
  const [referenciaBorradorConyuge, setReferenciaBorradorConyuge] = useState("");

  // (sigue existiendo soporte admin por si entras con rol ADMIN)
  const [adminColegiadoCodigo, setAdminColegiadoCodigo] = useState("");
  const [adminTitulo, setAdminTitulo] = useState("PENDIENTE");

  const [citasPropias, setCitasPropias] = useState([]);
  const [loadingPropias, setLoadingPropias] = useState(false);

  const [mensaje, setMensaje] = useState("");
  const [tipoMensaje, setTipoMensaje] = useState("info");

  // ---- Carga inicial: /api/citas ----
  const cargarTodas = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/citas`, { headers });
      const lista = Array.isArray(data) ? data : [];

      // ordenar por fechaInicio
      lista.sort((a, b) => {
        const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
        const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
        return aFi - bFi;
      });

      setTodas(lista);

      // min/max por primera/última entrada con fecha válida
      const validas = lista
        .map(c => toDay(pick(c, "fechaInicio", "fecha_inicio")))
        .filter(d => d && d.isValid())
        .map(d => d.valueOf());
      const min = validas.length ? dayjs(Math.min(...validas)) : null;
      const max = validas.length ? dayjs(Math.max(...validas)) : null;

      setMinDate(min ? min.startOf("day") : null);
      setMaxDate(max ? max.startOf("day") : null);

      const firstMonth = (min || dayjs()).startOf("month");
      setMonthCursor(firstMonth);
      setSelectedDate(min || dayjs());

      // badge DISPONIBLE por día
      recalcularConteoMes(lista, firstMonth, min, max);
      // huecos del día seleccionado
      recalcularHuecosDia(lista, min || dayjs());

    } catch (e) {
      console.error(e);
      setMensaje("No se pudieron cargar las citas.");
      setTodas([]);
      setMinDate(null);
      setMaxDate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarTodas(); /* eslint-disable-next-line */ }, []);

  // ---- Recalcula conteo por día del mes (solo DISPONIBLE) ----
  const recalcularConteoMes = (lista, monthRef, min, max) => {
    if (!monthRef) { setMapCountByDay({}); return; }
    const start = monthRef.startOf("month");
    const end = monthRef.endOf("month");

    const counts = {};
    for (const c of lista) {
      if ((c.titulo || "").toUpperCase() !== "DISPONIBLE") continue;
      const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
      if (!fi) continue;
      if (min && fi.isBefore(min, "day")) continue;
      if (max && fi.isAfter(max, "day")) continue;
      if (fi.isBefore(start, "day") || fi.isAfter(end, "day")) continue;

      const key = fi.format("YYYY-MM-DD");
      counts[key] = (counts[key] || 0) + 1;
    }
    setMapCountByDay(counts);
  };

  // ---- Recalcula huecos del día seleccionado ----
  const recalcularHuecosDia = (lista, fecha) => {
    if (!fecha) { setHuecosDia([]); return; }
    const ymd = dayjs(fecha).format("YYYY-MM-DD");
    const delDia = lista.filter(c => {
      const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
      return fi && fi.format("YYYY-MM-DD") === ymd;
    });

    // ordenar por hora de inicio
    delDia.sort((a, b) => {
      const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
      const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
      return aFi - bFi;
    });

    setHuecosDia(delDia);
    setSlotSel(null);
  };

  useEffect(() => { if (monthCursor) recalcularConteoMes(todas, monthCursor, minDate, maxDate); }, [monthCursor, todas, minDate, maxDate]);
  useEffect(() => { if (selectedDate) recalcularHuecosDia(todas, selectedDate); }, [selectedDate, todas]);

  // ---- Mis citas ----
  const cargarPropias = async () => {
    if (!usuario?.codigo) return;
    setLoadingPropias(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/citas/colegiado/${usuario.codigo}`,
        { headers }
      );
      setCitasPropias(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMensaje("Error al obtener tus citas.");
    } finally {
      setLoadingPropias(false);
    }
  };
  useEffect(() => { if (usuario?.codigo) cargarPropias(); }, [usuario?.codigo]); // solo cuando haya login

  const activosCount = useMemo(() => {
    const list = Array.isArray(citasPropias) ? citasPropias : [];
    return list.filter(c => ["PENDIENTE", "CONFIRMADA"].includes(String(c.titulo || "").toUpperCase())).length;
  }, [citasPropias]);

  // Busca consecutivo exacto con mismo colegiado (para anular conjuntos)
  const findNextConsecutiveSlot = (slotsDelDia, slotActual, colegiadoCodigoTarget = null) => {
    if (!slotActual) return null;
    const finActual = toDay(pick(slotActual, "fechaFin", "fecha_fin"));
    if (!finActual) return null;
    const finMs = finActual.valueOf();
    return (
      slotsDelDia.find((s) => {
        const ini = toDay(pick(s, "fechaInicio", "fecha_inicio"));
        const coleg =
          s?.colegiado?.codigo ?? s?.colegiadoCodigo ?? s?.colegiado_id ?? s?.colegiadoId ?? null;
        const sameColegiado = colegiadoCodigoTarget ? Number(coleg) === Number(colegiadoCodigoTarget) : true;
        return ini && sameColegiado && ini.valueOf() === finMs;
      }) || null
    );
  };

  // Consecutivo DISPONIBLE (para reservar cónyuge)
  const findNextConsecutiveDisponible = (slotsDelDia, slotActual) => {
    if (!slotActual) return null;
    const finActual = toDay(pick(slotActual, "fechaFin", "fecha_fin"));
    if (!finActual) return null;
    const finMs = finActual.valueOf();
    return (
      slotsDelDia.find((s) => {
        const ini = toDay(pick(s, "fechaInicio", "fecha_inicio"));
        const titulo = String(s.titulo || "").toUpperCase();
        const coleg =
          s?.colegiado?.codigo ?? s?.colegiadoCodigo ?? s?.colegiado_id ?? s?.colegiadoId ?? null;
        return ini && ini.valueOf() === finMs && titulo === "DISPONIBLE" && (coleg == null);
      }) || null
    );
  };

  // ---- Confirmar / actualizar cita(s) ----
  const confirmarSolicitud = async () => {
    const esAdmin = (usuario?.rol || "").toUpperCase() === "ADMIN";

    const colegiadoCodigo = esAdmin && adminColegiadoCodigo
      ? Number(adminColegiadoCodigo)
      : Number(usuario?.codigo || 0);

    if (!slotSel?.id) return setMensaje("Elige un hueco.");
    if (!colegiadoCodigo) return setMensaje("No hay colegiado seleccionado.");

    // --- Restricción de cupo para usuario ---
    if (!esAdmin) {
      const add = conyuge ? 2 : 1;
      const cap = conyuge ? 2 : 1;
      if (activosCount + add > cap) {
        return setMensaje(
          conyuge
            ? `No puedes reservar: ya tienes ${activosCount} hueco(s) activo(s) y el límite con cónyuge es 2.`
            : `No puedes reservar: ya tienes ${activosCount} hueco(s) activo(s) y el límite sin cónyuge es 1.`
        );
      }
    }

    // consecutivo para cónyuge -> debe ser DISPONIBLE inmediatamente después
    const consecutivo = conyuge ? findNextConsecutiveDisponible(huecosDia, slotSel) : null;
    if (conyuge && !consecutivo) {
      return setMensaje("No existe un hueco consecutivo inmediato para cónyuge.");
    }

    const titulo = isAdmin ? (adminTitulo || "PENDIENTE") : "PENDIENTE";

    const fiSel = toDay(pick(slotSel, "fechaInicio", "fecha_inicio"));
    const ffSel = toDay(pick(slotSel, "fechaFin", "fecha_fin"));

    // —— Payload con Variantes (camel y snake) + nuevos campos ——
    const addRentaFields = (obj = {}) => ({
      ...obj,
      // titular
      fechaCaducidadNif: fechaCaducidadNif ? dayjs(fechaCaducidadNif).format("YYYY-MM-DD") : null,
      fecha_caducidad_nif: fechaCaducidadNif ? dayjs(fechaCaducidadNif).format("YYYY-MM-DD") : null,
      referenciaBorrador: referenciaBorrador || null,
      referencia_borrador: referenciaBorrador || null,
      rentaAnterior: !!rentaAnterior,
      renta_anterior: !!rentaAnterior,
      // cónyuge
      nombreConyuge: conyuge ? (nombreConyuge || null) : null,
      apellidosConyuge: conyuge ? (apellidosConyuge || null) : null,
      nifConyuge: conyuge ? (nifConyuge || null) : null,
      fechaCaducidadNifConyuge: conyuge && fechaCaducidadNifConyuge ? dayjs(fechaCaducidadNifConyuge).format("YYYY-MM-DD") : null,
      referenciaBorradorConyuge: conyuge ? (referenciaBorradorConyuge || null) : null,
      // snake_case duplicado
      nombre_conyuge: conyuge ? (nombreConyuge || null) : null,
      apellidos_conyuge: conyuge ? (apellidosConyuge || null) : null,
      nif_conyuge: conyuge ? (nifConyuge || null) : null,
      fecha_caducidad_nif_conyuge: conyuge && fechaCaducidadNifConyuge ? dayjs(fechaCaducidadNifConyuge).format("YYYY-MM-DD") : null,
      referencia_borrador_conyuge: conyuge ? (referenciaBorradorConyuge || null) : null,
    });

    // Importante: usamos la DESCRIPCIÓN del hueco (tipo de cita) tal y como viene de BBDD
    const payloadPrincipal = addRentaFields({
      titulo,
      descripcion: slotSel?.descripcion || "", // CITA PRESENCIAL / CITA TELEFÓNICA / etc.
      fechaInicio: isoLocal(fiSel),
      fechaFin: isoLocal(ffSel),
      colegiadoCodigo,
      colegiadoId: colegiadoCodigo,
      colegiado_id: colegiadoCodigo,
      colegiado: { codigo: colegiadoCodigo },
    });

    const putCita = async (slot, payload) =>
      axios.put(`${process.env.REACT_APP_API_URL}/api/citas/${slot.id}`, payload, { headers });

    try {
      if (consecutivo) {
        const fi2 = toDay(pick(consecutivo, "fechaInicio", "fecha_inicio"));
        const ff2 = toDay(pick(consecutivo, "fechaFin", "fecha_fin"));
        const payloadConyuge = addRentaFields({
          titulo,
          descripcion: consecutivo?.descripcion || slotSel?.descripcion || "",
          fechaInicio: isoLocal(fi2),
          fechaFin: isoLocal(ff2),
          colegiadoCodigo,
          colegiadoId: colegiadoCodigo,
          colegiado_id: colegiadoCodigo,
          colegiado: { codigo: colegiadoCodigo },
        });

        await putCita(consecutivo, payloadConyuge);
        await putCita(slotSel, payloadPrincipal);
      } else {
        await putCita(slotSel, payloadPrincipal);
      }

      setMensaje("Cita actualizada correctamente.");
      setSlotSel(null);

      await cargarTodas();
      await cargarPropias();
    } catch (e) {
      console.error(e);

      if (e?.response?.status === 409) {
        setTipoMensaje("warning");
        setMensaje("Ese hueco acaba de ser reservado por otra persona. Selecciona otro horario disponible.");
        await cargarTodas();
        await cargarPropias();
        setSlotSel(null);
        return;
      }

      setTipoMensaje("error");
      setMensaje("La cita ha sido reservada por otra persona, actualice la página.");
    }
  };

  // Volver a DISPONIBLE (y si fue doble por cónyuge, anula ambas) manteniendo descripción original
  const volverDisponible = async (cita) => {
    try {
      const coleg =
        cita?.colegiado?.codigo ?? cita?.colegiadoCodigo ?? cita?.colegiado_id ?? cita?.colegiadoId ?? null;

      const fi = toDay(pick(cita, "fechaInicio", "fecha_inicio"));
      const ff = toDay(pick(cita, "fechaFin", "fecha_fin"));

      const baseBody = (ini, fin, desc) => ({
        titulo: "DISPONIBLE",
        descripcion: desc || "Hueco disponible", // mantenemos el tipo del día
        fechaInicio: isoLocal(ini),
        fechaFin: isoLocal(fin),
        fecha_inicio: isoLocal(ini),
        fecha_fin: isoLocal(fin),
        // limpiar colegiado y datos de renta/cónyuge
        colegiadoCodigo: null, colegiadoId: null, colegiado_id: null, colegiado: null,
        fechaCaducidadNif: null, fecha_caducidad_nif: null,
        referenciaBorrador: null, referencia_borrador: null,
        rentaAnterior: null, renta_anterior: null,
        nombreConyuge: null, apellidosConyuge: null, nifConyuge: null,
        fechaCaducidadNifConyuge: null, referenciaBorradorConyuge: null,
        nombre_conyuge: null, apellidos_conyuge: null, nif_conyuge: null,
        fecha_caducidad_nif_conyuge: null, referencia_borrador_conyuge: null,
      });

      // 1) liberar la cita actual (con su descripción original)
      await axios.put(`${process.env.REACT_APP_API_URL}/api/citas/${cita.id}`, baseBody(fi, ff, cita?.descripcion), { headers });

      // 2) si existe la pareja consecutiva con mismo colegiado, liberarla también
      const ymd = fi?.format("YYYY-MM-DD");
      const delDia = todas.filter((x) => {
        const d = toDay(pick(x, "fechaInicio", "fecha_inicio"));
        return d && d.format("YYYY-MM-DD") === ymd;
      });
      const pareja =
        findNextConsecutiveSlot(delDia, cita, coleg) ||
        delDia.find((s) => {
          const finOtra = toDay(pick(s, "fechaFin", "fecha_fin"));
          return (
            finOtra &&
            fi &&
            finOtra.valueOf() === fi.valueOf() &&
            (s?.colegiado?.codigo ?? s?.colegiadoCodigo ?? s?.colegiado_id ?? s?.colegiadoId ?? null) === coleg
          );
        }) ||
        null;

      if (pareja) {
        const pIni = toDay(pick(pareja, "fechaInicio", "fecha_inicio"));
        const pFin = toDay(pick(pareja, "fechaFin", "fecha_fin"));
        await axios.put(`${process.env.REACT_APP_API_URL}/api/citas/${pareja.id}`, baseBody(pIni, pFin, pareja?.descripcion), { headers });
      }

      setMensaje("Cita(s) anulada(s) y vuelta(s) a DISPONIBLE.");
      await cargarTodas();
      await cargarPropias();
    } catch (e) {
      console.error(e);
      setMensaje("No se pudo revertir la cita.");
    }
  };

  // ---- UI helpers ----
  const renderEstadoChip = (estado) => {
    const up = (estado || "").toUpperCase();
    if (up === "DISPONIBLE") return { variant: "outlined", disabled: false };
    return { variant: "outlined", disabled: true, sx: { color: "#888", borderColor: "#ccc" } };
  };

  const capTexto = !isAdmin
    ? (conyuge ? "Límite: 2 huecos (cita con cónyuge)" : "Límite: 1 hueco (sin cónyuge)")
    : null;
  const limiteExcedido = !isAdmin && (activosCount + (conyuge ? 2 : 1) > (conyuge ? 2 : 1));

  const tipoSeleccionado = (slotSel?.descripcion || "").toString().trim();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box p={2}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>Solicitar / gestionar citas</Typography>

          {loading ? (
            <CircularProgress />
          ) : !minDate || !maxDate ? (
            <Typography color="error">No hay citas definidas para mostrar.</Typography>
          ) : (
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "360px 1fr" }} gap={3}>
              {/* Calendario */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                  Rango visible: {minDate.format("DD/MM/YYYY")} – {maxDate.format("DD/MM/YYYY")}
                </Typography>

                <DateCalendar
                  value={selectedDate ?? minDate}
                  onChange={(v) => { if (v) setSelectedDate(v); }}
                  onMonthChange={(m) => { if (m) setMonthCursor(m); }}
                  defaultCalendarMonth={minDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  renderDay={(date, _selectedDates, pickersDayProps) => {
                    const key = dayjs(date).format("YYYY-MM-DD");
                    const count = mapCountByDay[key] || 0;
                    return (
                      <Badge
                        overlap="circular"
                        color="primary"
                        badgeContent={count}
                        invisible={!count}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <PickersDay {...pickersDayProps} />
                      </Badge>
                    );
                  }}
                />
              </Box>

              {/* Detalle del día + formulario */}
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Banner del tipo de cita seleccionado */}
                {slotSel && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f7faff" }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
                      {tipoSeleccionado || "CITA"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: .75 }}>
                      Esta descripción viene definida por la organización y no se puede modificar.
                    </Typography>
                  </Paper>
                )}

                {!isAdmin && (
                  <>
                    <Typography variant="h6">Datos para la Renta</Typography>

                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                      <DatePicker
                        label="Fecha caducidad NIF"
                        format="DD/MM/YYYY"
                        value={fechaCaducidadNif}
                        onChange={setFechaCaducidadNif}
                      />
                      <TextField
                        label="Referencia del Borrador"
                        value={referenciaBorrador}
                        onChange={(e) => setReferenciaBorrador(e.target.value)}
                      />
                    </Box>

                    <FormControlLabel
                      control={<Checkbox checked={rentaAnterior} onChange={(e) => setRentaAnterior(e.target.checked)} />}
                      label="¿Hiciste la renta con nosotros el año pasado?"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={conyuge}
                          onChange={(e) => setConyuge(e.target.checked)}
                        />
                      }
                      label="Cita con cónyuge (reserva dos huecos consecutivos)"
                    />

                    {conyuge && (
                      <Box sx={{ border: "1px dashed #ddd", borderRadius: 2, p: 2, mt: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Datos del cónyuge</Typography>
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                          <TextField
                            label="Nombre cónyuge"
                            value={nombreConyuge}
                            onChange={(e) => setNombreConyuge(e.target.value)}
                          />
                          <TextField
                            label="Apellidos cónyuge"
                            value={apellidosConyuge}
                            onChange={(e) => setApellidosConyuge(e.target.value)}
                          />
                          <TextField
                            label="NIF cónyuge"
                            value={nifConyuge}
                            onChange={(e) => setNifConyuge(e.target.value)}
                          />
                          <DatePicker
                            label="Caducidad NIF cónyuge"
                            format="DD/MM/YYYY"
                            value={fechaCaducidadNifConyuge}
                            onChange={setFechaCaducidadNifConyuge}
                          />
                          <TextField
                            label="Referencia borrador (cónyuge)"
                            value={referenciaBorradorConyuge}
                            onChange={(e) => setReferenciaBorradorConyuge(e.target.value)}
                          />
                        </Box>
                      </Box>
                    )}

                    <Typography variant="caption" fontSize={15} fontStyle="italic" fontWeight={600} sx={{ opacity: 0.8 }}>
                      {conyuge ? "Límite: 2 huecos (cita con cónyuge)" : "Límite: 1 hueco (sin cónyuge)"} · Tus reservas activas: {activosCount}
                    </Typography>
                  </>
                )}

                <Typography variant="h6">
                  {selectedDate ? `Huecos del ${dayjs(selectedDate).format("DD/MM/YYYY")}` : "Selecciona una fecha"}
                </Typography>

                {!selectedDate ? (
                  <Typography variant="body2">Elige una fecha en el calendario.</Typography>
                ) : huecosDia.length === 0 ? (
                  <Typography variant="body2">No hay citas en este día.</Typography>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1 }}>
                    {huecosDia.map((h) => {
                      const fi = toDay(pick(h, "fechaInicio", "fecha_inicio"));
                      const ff = toDay(pick(h, "fechaFin", "fecha_fin"));
                      const selected = slotSel?.id === h.id;
                      const chip = renderEstadoChip(h.titulo);

                      return (
                        <Button
                          key={h.id}
                          variant={selected ? "contained" : chip.variant}
                          onClick={() => !chip.disabled && setSlotSel(h)}
                          disabled={chip.disabled}
                          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", ...(chip.sx || {}) }}
                        >
                          <span style={{ fontWeight: 700 }}>{fi?.format("HH:mm")}–{ff?.format("HH:mm")}</span>
                          <span style={{ fontSize: 12, opacity: 0.8 }}>{h.descripcion || "—"}</span>
                        </Button>
                      );
                    })}
                  </Box>
                )}

                <Box>
                  <Button
                    variant="contained"
                    onClick={confirmarSolicitud}
                    disabled={
                      !slotSel ||
                      !(isAdmin || usuario?.codigo) ||
                      (!!limiteExcedido)
                    }
                    title={limiteExcedido ? "Has alcanzado el límite de reservas para esta modalidad" : ""}
                  >
                    {isAdmin ? "Guardar cambios" : "Confirmar solicitud"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* --- Mis citas --- */}
        <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h5" gutterBottom>Mis citas</Typography>
          {loadingPropias ? (
            <CircularProgress />
          ) : citasPropias.length === 0 ? (
            <Typography>No tienes citas registradas.</Typography>
          ) : (
            citasPropias
              .sort((a, b) => {
                const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                return aFi - bFi;
              })
              .map((cita) => {
                const fi = toDay(pick(cita, "fechaInicio", "fecha_inicio"));
                const ff = toDay(pick(cita, "fechaFin", "fecha_fin"));
                const estadoUp = String(cita.titulo || "").toUpperCase();
                const puedeRevertir = estadoUp === "PENDIENTE";
                return (
                  <Box key={cita.id} p={2} border="1px solid #ccc" borderRadius={2} mb={1}>
                    <Typography><strong>Estado:</strong> {cita.titulo}</Typography>
                    <Typography><strong>Tipo:</strong> {cita.descripcion || "—"}</Typography>
                    <Typography><strong>Inicio:</strong> {fi?.format("DD/MM/YYYY HH:mm")}</Typography>
                    <Typography><strong>Fin:</strong> {ff?.format("DD/MM/YYYY HH:mm")}</Typography>
                    {puedeRevertir && (
                      <Box mt={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => volverDisponible(cita)}
                        >
                          ANULAR CITA
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })
          )}
        </Paper>

        <Snackbar open={!!mensaje} autoHideDuration={4000} onClose={() => setMensaje("")} message={mensaje} />
      </Box>
    </LocalizationProvider>
  );
}
